import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n, StatusBadge, GsdReadableBlock } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { useDataStore } from "@/components/admin/useDataStore.js";
import { useAuth } from "@/context/AuthContext.jsx";
import {
  validateAuthorityDocument,
  updateAuthorityDocument,
  updateEvidenceDossier,
  appendValidationLog,
  addAuthorityDocument,
} from "@/data/dataStore.js";
import { notifyDataUpdate } from "@/components/admin/useDataStore.js";
import { InternalReferentialNotice } from "@/components/titles/KarateTitleRecognitionPanels.jsx";
import {
  DOCUMENT_TYPES,
  DOCUMENT_VALIDATION_STATUSES,
  SENSITIVITY_LEVELS,
  DOSSIER_STATUSES,
  LOG_ACTIONS,
  authDocLabel,
} from "@/utils/authorityDocumentLabels.js";

function useLocale() {
  const { lang } = useI18n();
  return lang === "en" ? "en" : "fr";
}

export function SecurityFuturePanel({ testId = "security-future-panel" }) {
  const locale = useLocale();
  return (
    <GsdReadableBlock testId={testId}>
      <h3>{locale === "en" ? "Confidentiality & future security" : "Confidentialité / sécurité future"}</h3>
      <p className="muted">
        {locale === "en"
          ? "Authority documents may be sensitive. A future version will require backend authentication, strict roles, secure storage, immutable audit trail, access control, backups, consent and personal data protection."
          : "Les documents d'autorité peuvent être sensibles. Une version future devra prévoir authentification backend, rôles stricts, stockage sécurisé, historique non modifiable, contrôle des accès, sauvegardes, consentement et protection des renseignements personnels."}
      </p>
      <p className="muted" data-testid="security-not-implemented">
        {locale === "en"
          ? "Not implemented in Phase 1 — local demo only."
          : "Non développé en Phase 1 — démo locale seulement."}
      </p>
    </GsdReadableBlock>
  );
}

function docBadge(doc, locale) {
  if (doc.statut_demo === "a_fournir" || doc.statut_validation === "a_fournir") {
    return locale === "en" ? "[TO PROVIDE]" : "[À FOURNIR]";
  }
  if (doc.demo) return "[DÉMO]";
  return "";
}

function getAuthorityModuleLie(doc, locale) {
  if (doc.categorie === "dojo_general") {
    return locale === "en" ? "General dojo" : "Dojo général";
  }
  return "Shotokan Black Dragon";
}

function getCategorieLabel(categorie, locale) {
  const map = {
    autorite: { fr: "Autorité", en: "Authority" },
    demande_titre: { fr: "Demande de titre", en: "Title application" },
    reconnaissance: { fr: "Reconnaissance", en: "Recognition" },
    dojo_general: { fr: "Dojo général", en: "General dojo" },
  };
  const entry = map[categorie];
  if (!entry) return categorie;
  return locale === "en" ? entry.en : entry.fr;
}

export function CentralDocumentsLink({ testId = "central-documents-link" }) {
  const locale = useLocale();
  return (
    <GsdReadableBlock testId={testId}>
      <p>
        <Link to="/admin/documents-preuves" data-testid={`${testId}-href`}>
          {locale === "en"
            ? "View all documents in Documents & Evidence"
            : "Voir tous les documents dans Documents & Preuves"}
        </Link>
      </p>
    </GsdReadableBlock>
  );
}

export function AuthorityDocumentsPanel({
  mode = "admin",
  scope = "all",
  testId = "authority-documents-panel",
}) {
  const locale = useLocale();
  const store = useDataStore();
  const { username } = useAuth();
  const canAdmin = mode === "admin";

  const rows = useMemo(() => {
    let list = store.authorityDocuments || [];
    if (mode === "sensei") {
      list = list.filter(
        (d) =>
          d.demande_titre_id?.startsWith("app-demo") &&
          (d.ajoute_par === "demo-sensei-placeholder" || d.demande_titre_id === "app-demo-sensei-01"),
      );
    }
    if (mode === "sempai") {
      list = list.filter((d) => d.statut_demo !== "a_fournir" || d.type_document === "lettre_recommandation");
    }
    if (scope === "shotokan") {
      list = list.filter((d) => d.categorie !== "dojo_general");
    }

    const authRows = list.map((d) => ({
      ...d,
      sourceKind: "authority",
      module_lie: getAuthorityModuleLie(d, locale),
      categorieLabel: getCategorieLabel(d.categorie, locale),
      typeLabel: authDocLabel(DOCUMENT_TYPES, d.type_document, locale),
      statutLabel: authDocLabel(DOCUMENT_VALIDATION_STATUSES, d.statut_validation, locale),
      sensLabel: authDocLabel(SENSITIVITY_LEVELS, d.niveau_sensibilite, locale),
    }));

    if (scope !== "all" || mode !== "admin") {
      return authRows;
    }

    const dojoRows = (store.dojoDocuments || []).map((d) => ({
      ...d,
      sourceKind: "dojo",
      module_lie: d.module_lie || (locale === "en" ? "General dojo" : "Dojo général"),
      categorieLabel: getCategorieLabel(d.categorie, locale),
      typeLabel: authDocLabel(DOCUMENT_TYPES, d.type_document, locale),
      statutLabel: authDocLabel(DOCUMENT_VALIDATION_STATUSES, d.statut_validation, locale),
      sensLabel: authDocLabel(SENSITIVITY_LEVELS, d.niveau_sensibilite, locale),
      demande_titre_id: null,
    }));

    return [...authRows, ...dojoRows];
  }, [store.authorityDocuments, store.dojoDocuments, mode, scope, locale]);

  const handleAction = (id, action) => {
    if (!canAdmin) return;
    validateAuthorityDocument(id, action, username, "hanshi");
    notifyDataUpdate();
  };

  const columns = [
    {
      key: "titre",
      label: locale === "en" ? "Title" : "Titre",
      render: (r) => (
        <>
          {r.titre} {docBadge(r, locale) && <span className="muted">{docBadge(r, locale)}</span>}
          {r.source === "import client" ? (
            <span className="muted" data-testid={`doc-source-import-${r.id}`}>
              {" "}
              {locale === "en" ? "[CLIENT IMPORT]" : "[IMPORT CLIENT]"}
            </span>
          ) : null}
        </>
      ),
    },
    { key: "typeLabel", label: locale === "en" ? "Type" : "Type" },
    ...(scope === "all" && mode === "admin"
      ? [{ key: "categorieLabel", label: locale === "en" ? "Category" : "Catégorie" }]
      : []),
    { key: "personne_liee", label: locale === "en" ? "Person" : "Personne" },
    ...(scope === "all" && mode === "admin"
      ? [{ key: "module_lie", label: locale === "en" ? "Linked module" : "Module lié" }]
      : []),
    ...(scope === "shotokan" || (scope === "all" && mode !== "admin")
      ? [
          {
            key: "demande_titre_id",
            label: locale === "en" ? "Application" : "Demande",
            render: (r) => r.demande_titre_id ?? "—",
          },
        ]
      : []),
    {
      key: "statutLabel",
      label: locale === "en" ? "Status" : "Statut",
      render: (r) => (
        <StatusBadge label={r.statutLabel} tone={r.statut_validation === "a_fournir" ? "yellow" : "blue"} testId={`doc-status-${r.id}`} />
      ),
    },
    { key: "sensLabel", label: locale === "en" ? "Sensitivity" : "Sensibilité" },
    { key: "date_ajout", label: locale === "en" ? "Added" : "Ajout" },
  ];

  return (
    <div data-testid={testId}>
      <InternalReferentialNotice />
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["titre", "personne_liee", "typeLabel", "module_lie", "categorieLabel"]}
        filters={[
          ...(scope === "all" && mode === "admin"
            ? [
                {
                  field: "module_lie",
                  label: locale === "en" ? "Linked module" : "Module lié",
                  options: [
                    { value: "Shotokan Black Dragon", label: "Shotokan Black Dragon" },
                    { value: locale === "en" ? "General dojo" : "Dojo général", label: locale === "en" ? "General dojo" : "Dojo général" },
                  ],
                },
              ]
            : []),
          {
            field: "statut_validation",
            label: locale === "en" ? "Status" : "Statut",
            options: Object.entries(DOCUMENT_VALIDATION_STATUSES).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
          {
            field: "type_document",
            label: locale === "en" ? "Type" : "Type",
            options: Object.entries(DOCUMENT_TYPES).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
          {
            field: "niveau_sensibilite",
            label: locale === "en" ? "Sensitivity" : "Sensibilité",
            options: Object.entries(SENSITIVITY_LEVELS).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No documents." : "Aucun document."}
        testId={`${testId}-table`}
      />
      {canAdmin ? (
        <div className="btn-row" data-testid="admin-doc-actions" style={{ marginTop: "1rem" }}>
          {rows
            .filter((r) => r.sourceKind !== "dojo")
            .slice(0, 3)
            .map((r) => (
            <span key={r.id} className="doc-action-group">
              <button type="button" className="btn-secondary btn-sm" data-testid={`doc-view-${r.id}`}>
                {locale === "en" ? "View" : "Voir"}
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={() => handleAction(r.id, "accept")} data-testid={`doc-accept-${r.id}`}>
                {locale === "en" ? "Accept" : "Accepter"}
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={() => handleAction(r.id, "refuse")} data-testid={`doc-refuse-${r.id}`}>
                {locale === "en" ? "Refuse" : "Refuser"}
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={() => handleAction(r.id, "replace")} data-testid={`doc-replace-${r.id}`}>
                {locale === "en" ? "Request replacement" : "Demander remplacement"}
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => {
                  updateAuthorityDocument(r.id, { statut_validation: "a_fournir" });
                  notifyDataUpdate();
                }}
                data-testid={`doc-mark-provide-${r.id}`}
              >
                {locale === "en" ? "Mark to provide" : "Marquer à fournir"}
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function EvidenceDossiersPanel({ testId = "evidence-dossiers-panel" }) {
  const locale = useLocale();
  const store = useDataStore();

  const rows = (store.evidenceDossiers || []).map((d) => ({
    ...d,
    statutLabel: authDocLabel(DOSSIER_STATUSES, d.statut_dossier, locale),
    missingCount: d.documents_manquants?.length ?? 0,
  }));

  const columns = [
    {
      key: "sujet_nom",
      label: locale === "en" ? "Subject" : "Sujet",
      render: (r) => (
        <>
          {r.sujet_nom} <DemoBadge />
        </>
      ),
    },
    { key: "type_dossier", label: locale === "en" ? "Type" : "Type" },
    {
      key: "score_completude",
      label: "%",
      render: (r) => (
        <span data-testid={`dossier-score-${r.id}`}>{r.score_completude}%</span>
      ),
    },
    { key: "statutLabel", label: locale === "en" ? "Status" : "Statut" },
    {
      key: "missingCount",
      label: locale === "en" ? "Missing" : "Manquants",
      render: (r) => (
        <span data-testid={`dossier-missing-${r.id}`}>{r.missingCount}</span>
      ),
    },
    {
      key: "decision_possible",
      label: locale === "en" ? "Decision" : "Décision",
      render: (r) => (r.decision_possible ? (locale === "en" ? "Yes" : "Oui") : (locale === "en" ? "No" : "Non")),
    },
  ];

  const handleDossierAction = (id, statut) => {
    updateEvidenceDossier(id, { statut_dossier: statut });
    appendValidationLog({
      id: `log-${Date.now()}`,
      date: new Date().toISOString(),
      acteur: "admin",
      role_acteur: "hanshi",
      action: "dossier_revise",
      cible_type: "evidenceDossier",
      cible_id: id,
      ancien_statut: null,
      nouveau_statut: statut,
      commentaire: `[DÉMO] Dossier → ${statut}`,
      document_id: null,
      demande_titre_id: null,
      visibilite: "interne",
      demo: true,
    });
    notifyDataUpdate();
  };

  return (
    <div data-testid={testId}>
      <InternalReferentialNotice />
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["sujet_nom", "type_dossier"]}
        filters={[
          {
            field: "statut_dossier",
            label: locale === "en" ? "Status" : "Statut",
            options: Object.entries(DOSSIER_STATUSES).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No dossiers." : "Aucun dossier."}
        testId={`${testId}-table`}
      />
      <div className="btn-row" style={{ marginTop: "1rem" }}>
        <button type="button" className="btn-secondary btn-sm" onClick={() => handleDossierAction("dossier-auth-responsable", "en_revision")} data-testid="dossier-mark-review">
          {locale === "en" ? "Mark authority dossier in review" : "Marquer dossier autorité en révision"}
        </button>
      </div>
    </div>
  );
}

export function ValidationLogPanel({ testId = "validation-log-panel" }) {
  const locale = useLocale();
  const store = useDataStore();

  const rows = (store.validationLog || []).map((l) => ({
    ...l,
    actionLabel: authDocLabel(LOG_ACTIONS, l.action, locale) || l.action,
  }));

  const columns = [
    { key: "date", label: locale === "en" ? "Date" : "Date", render: (r) => r.date?.slice(0, 16) },
    { key: "acteur", label: locale === "en" ? "Actor" : "Acteur" },
    { key: "actionLabel", label: locale === "en" ? "Action" : "Action" },
    { key: "cible_id", label: locale === "en" ? "Target" : "Cible" },
    { key: "ancien_statut", label: locale === "en" ? "From" : "De" },
    { key: "nouveau_statut", label: locale === "en" ? "To" : "Vers" },
    { key: "commentaire", label: locale === "en" ? "Comment" : "Commentaire" },
  ];

  return (
    <div data-testid={testId}>
      <p className="muted" data-testid="validation-log-demo-notice">
        {locale === "en"
          ? "Local demo journal — official traceability will require a secure backend."
          : "Journal local de démonstration — la traçabilité officielle nécessitera un backend sécurisé."}
      </p>
      <InternalReferentialNotice />
      <DataTable
        rows={rows}
        columns={columns}
        searchFields={["acteur", "cible_id", "commentaire"]}
        filters={[
          {
            field: "action",
            label: locale === "en" ? "Action" : "Action",
            options: Object.entries(LOG_ACTIONS).map(([v, l]) => ({
              value: v,
              label: locale === "en" ? l.en : l.fr,
            })),
          },
          {
            field: "acteur",
            label: locale === "en" ? "Actor" : "Acteur",
            options: [...new Set((store.validationLog || []).map((l) => l.acteur))].map((a) => ({
              value: a,
              label: a,
            })),
          },
          {
            field: "visibilite",
            label: locale === "en" ? "Visibility" : "Visibilité",
            options: [...new Set((store.validationLog || []).map((l) => l.visibilite).filter(Boolean))].map((v) => ({
              value: v,
              label: v,
            })),
          },
        ]}
        emptyMessage={locale === "en" ? "No log entries." : "Aucune entrée."}
        testId={`${testId}-table`}
      />
    </div>
  );
}

/** Bloc résumé dossier/preuves pour intégration BT-018 */
export function EvidenceSummaryBlock({ sujetId, testId = "evidence-summary" }) {
  const locale = useLocale();
  const store = useDataStore();
  const dossier = store.evidenceDossiers?.find((d) => d.sujet_id === sujetId);
  if (!dossier) return null;

  const missingDocs = (dossier.documents_manquants || [])
    .map((id) => store.authorityDocuments?.find((d) => d.id === id)?.titre ?? id);

  return (
    <GsdReadableBlock testId={testId}>
      <h4>{locale === "en" ? "Evidence dossier" : "Dossier de preuves"}</h4>
      <p data-testid={`${testId}-score`}>
        {locale === "en" ? "Completeness" : "Complétude"} : <strong>{dossier.score_completude}%</strong> —{" "}
        {authDocLabel(DOSSIER_STATUSES, dossier.statut_dossier, locale)}
      </p>
      {missingDocs.length ? (
        <p className="muted" data-testid={`${testId}-missing`}>
          {locale === "en" ? "Missing documents" : "Documents manquants"} : {missingDocs.join(", ")}
        </p>
      ) : (
        <p className="muted">{locale === "en" ? "No missing documents listed." : "Aucun document manquant listé."}</p>
      )}
      <p>
        <Link to="/admin/documents-preuves/dossiers" data-testid={`${testId}-link-dossiers-central`}>
          {locale === "en" ? "Central evidence dossiers" : "Dossiers centralisés"}
        </Link>
        {" · "}
        <Link to="/admin/shotokan-black-dragon/dossiers-preuves" data-testid={`${testId}-link-dossiers`}>
          {locale === "en" ? "Shotokan dossiers" : "Dossiers Shotokan"}
        </Link>
        {" · "}
        <Link to="/admin/documents-preuves/documents" data-testid={`${testId}-link-docs-central`}>
          {locale === "en" ? "All documents" : "Tous les documents"}
        </Link>
        {" · "}
        <Link to="/admin/shotokan-black-dragon/documents" data-testid={`${testId}-link-docs`}>
          {locale === "en" ? "Shotokan documents" : "Documents Shotokan"}
        </Link>
      </p>
    </GsdReadableBlock>
  );
}

export function SenseiTitleDocumentsPanel({ testId = "sensei-title-documents-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const [newDocTitle, setNewDocTitle] = useState("");

  const myDocs = (store.authorityDocuments || []).filter(
    (d) => d.demande_titre_id === "app-demo-sensei-01" || d.ajoute_par === "demo-sensei-placeholder",
  );

  const handleAddDemo = () => {
    const id = `doc-sensei-${Date.now()}`;
    addAuthorityDocument({
      id,
      titre: newDocTitle || `[DÉMO] Document ${id.slice(-6)}`,
      type_document: "autre",
      categorie: "demande_titre",
      personne_liee: "[DÉMO] Sensei démo",
      demande_titre_id: "app-demo-sensei-01",
      titre_id: "title-sensei",
      autorite_id: null,
      reconnaissance_id: null,
      fichier_nom: `${id}.pdf`,
      fichier_type: "application/pdf",
      date_document: new Date().toISOString().slice(0, 10),
      date_ajout: new Date().toISOString().slice(0, 10),
      ajoute_par: "demo-sensei-placeholder",
      statut_validation: "recu",
      niveau_sensibilite: "interne",
      notes: "[DÉMO] Ajout local — pas de fichier réel.",
      source: "[DÉMO]",
      statut_demo: "demo",
      demo: true,
    });
    notifyDataUpdate();
    setNewDocTitle("");
  };

  const handleRemove = (id) => {
    updateAuthorityDocument(id, { statut_validation: "a_remplacer", notes: "[DÉMO] Retiré par Sensei." });
    notifyDataUpdate();
  };

  return (
    <div data-testid={testId}>
      <InternalReferentialNotice />
      <p className="muted" data-testid="sensei-doc-prudence">
        {locale === "en"
          ? "Required proofs for your applications — you cannot self-validate."
          : "Preuves requises pour vos demandes — vous ne pouvez pas auto-valider."}
      </p>
      <EvidenceSummaryBlock sujetId="app-demo-sensei-01" testId="sensei-app-evidence" />
      <ul data-testid="sensei-doc-list">
        {myDocs.map((d) => (
          <li key={d.id}>
            {d.titre} — {authDocLabel(DOCUMENT_VALIDATION_STATUSES, d.statut_validation, locale)}{" "}
            <button type="button" className="btn-link btn-sm" onClick={() => handleRemove(d.id)} data-testid={`sensei-remove-doc-${d.id}`}>
              {locale === "en" ? "Remove demo" : "Retirer démo"}
            </button>
          </li>
        ))}
      </ul>
      <div className="btn-row">
        <input placeholder={locale === "en" ? "Demo doc title" : "Titre document démo"} value={newDocTitle} onChange={(e) => setNewDocTitle(e.target.value)} data-testid="sensei-add-doc-input" />
        <button type="button" className="btn-primary btn-sm" onClick={handleAddDemo} data-testid="sensei-add-doc-btn">
          {locale === "en" ? "Add demo document" : "Ajouter document démo"}
        </button>
      </div>
    </div>
  );
}

export function SempaiTitleDocumentsPanel({ testId = "sempai-title-documents-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const examples = (store.authorityDocuments || []).filter((d) => d.statut_demo === "demo").slice(0, 3);

  return (
    <GsdReadableBlock testId={testId}>
      <InternalReferentialNotice />
      <p data-testid="sempai-doc-no-approve">
        {locale === "en"
          ? "Examples of proofs needed to progress — consultation only, no approval."
          : "Exemples de preuves nécessaires pour progresser — consultation seule, pas d'approbation."}
      </p>
      <ul>
        {examples.map((d) => (
          <li key={d.id}>
            {authDocLabel(DOCUMENT_TYPES, d.type_document, locale)} — {d.titre}
          </li>
        ))}
      </ul>
    </GsdReadableBlock>
  );
}

export function ValidationWorkflowPanel({ testId = "validation-workflow-panel" }) {
  const locale = useLocale();
  const store = useDataStore();
  const docs = [...(store.authorityDocuments || []), ...(store.dojoDocuments || [])];
  const dossiers = store.evidenceDossiers || [];
  const importQueue = store.documentImportQueue || [];
  const importWarnings = store.documentImportWarnings || [];

  const importedDocs = docs.filter(
    (d) => d.source === "import client" || d.statut_validation === "recu_validation_requise",
  );
  const importsWithoutDossier = importQueue.filter(
    (i) => !i.dossier_lie_propose && i.statut_import !== "annule",
  );
  const importIdsWithWarnings = new Set(importWarnings.filter((w) => w.niveau === "attention" || w.bloquant).map((w) => w.import_id));
  const importsWithWarnings = importQueue.filter(
    (i) => importIdsWithWarnings.has(i.id) && i.statut_import !== "annule",
  );

  const sections = [
    {
      id: "imported-to-review",
      titleFr: "Documents importés à réviser",
      titleEn: "Imported documents to review",
      items: importedDocs,
    },
    {
      id: "imports-without-dossier",
      titleFr: "Documents sans dossier lié",
      titleEn: "Documents without linked dossier",
      items: importsWithoutDossier.map((i) => ({ id: i.id, titre: i.fichier_nom })),
    },
    {
      id: "imports-blocked-warning",
      titleFr: "Documents bloqués par avertissement",
      titleEn: "Documents blocked by warning",
      items: importsWithWarnings.map((i) => ({ id: i.id, titre: i.fichier_nom })),
    },
    {
      id: "to-provide",
      titleFr: "Documents à fournir",
      titleEn: "Documents to provide",
      items: docs.filter((d) => d.statut_validation === "a_fournir"),
    },
    {
      id: "to-review",
      titleFr: "Documents reçus à réviser",
      titleEn: "Received documents to review",
      items: docs.filter((d) => d.statut_validation === "recu" || d.statut_validation === "en_revision"),
    },
    {
      id: "incomplete-dossiers",
      titleFr: "Dossiers incomplets",
      titleEn: "Incomplete dossiers",
      items: dossiers.filter((d) => d.statut_dossier === "incomplet" || (d.score_completude ?? 100) < 100),
    },
    {
      id: "ready-decision",
      titleFr: "Dossiers prêts pour décision",
      titleEn: "Dossiers ready for decision",
      items: dossiers.filter((d) => d.decision_possible || d.statut_dossier === "pret_decision"),
    },
    {
      id: "blocked",
      titleFr: "Décisions bloquées par preuve manquante",
      titleEn: "Decisions blocked by missing proof",
      items: dossiers.filter((d) => !d.decision_possible && (d.documents_manquants?.length ?? 0) > 0),
    },
  ];

  const mockAction = (label, actionId) => (
    <button type="button" className="btn-secondary btn-sm" data-testid={actionId}>
      {label}
    </button>
  );

  return (
    <div data-testid={testId}>
      <InternalReferentialNotice />
      <p className="muted" data-testid="validation-workflow-demo-notice">
        {locale === "en"
          ? "Local demo workflow — no official validation. Mock actions only."
          : "Workflow local de démonstration — aucune validation officielle. Actions mock seulement."}
      </p>
      {sections.map((section) => (
        <GsdReadableBlock key={section.id} testId={`${testId}-${section.id}`}>
          <h4>{locale === "en" ? section.titleEn : section.titleFr}</h4>
          {section.items.length === 0 ? (
            <p className="muted">{locale === "en" ? "None in this section." : "Aucun élément dans cette section."}</p>
          ) : (
            <ul data-testid={`${testId}-list-${section.id}`}>
              {section.items.map((item) => (
                <li key={item.id}>
                  {item.titre || item.sujet_nom}{" "}
                  <DemoBadge />
                </li>
              ))}
            </ul>
          )}
          <div className="btn-row" style={{ marginTop: "0.5rem" }}>
            {mockAction(locale === "en" ? "Review demo" : "Réviser démo", `${testId}-review-${section.id}`)}
            {mockAction(locale === "en" ? "Accept demo" : "Accepter démo", `${testId}-accept-${section.id}`)}
            {mockAction(locale === "en" ? "Refuse demo" : "Refuser démo", `${testId}-refuse-${section.id}`)}
            {mockAction(locale === "en" ? "Postpone decision" : "Reporter décision", `${testId}-postpone-${section.id}`)}
          </div>
        </GsdReadableBlock>
      ))}
    </div>
  );
}

export function DocumentsPreuvesSecuritePanel({ testId = "documents-preuves-securite-panel" }) {
  const locale = useLocale();
  const futureItemsFr = [
    "Authentification backend",
    "Rôles stricts",
    "Stockage sécurisé",
    "Chiffrement des fichiers",
    "Journal immuable",
    "Contrôle des accès",
    "Sauvegardes",
    "Consentement",
    "Protection des renseignements personnels",
    "Séparation public / interne / confidentiel / très confidentiel",
    "Accès restreint aux documents d'autorité",
  ];
  const futureItemsEn = [
    "Backend authentication",
    "Strict roles",
    "Secure storage",
    "File encryption",
    "Immutable audit trail",
    "Access control",
    "Backups",
    "Consent",
    "Personal data protection",
    "Public / internal / confidential / highly confidential separation",
    "Restricted access to authority documents",
  ];
  const notDoneFr = [
    "Pas d'upload réel",
    "Pas de stockage serveur",
    "Pas de chiffrement réel",
    "Pas d'audit immuable réel",
    "Pas de certificat officiel",
  ];
  const notDoneEn = [
    "No real upload",
    "No server storage",
    "No real encryption",
    "No real immutable audit",
    "No official certificate",
  ];

  return (
    <div data-testid={testId}>
      <GsdReadableBlock testId={`${testId}-future`}>
        <h3>{locale === "en" ? "Future security requirements" : "Exigences de sécurité future"}</h3>
        <p className="muted">
          {locale === "en"
            ? "The official version will require the following safeguards for sensitive evidence documents."
            : "La version officielle devra prévoir les garde-fous suivants pour les documents de preuve sensibles."}
        </p>
        <ul data-testid={`${testId}-future-list`}>
          {(locale === "en" ? futureItemsEn : futureItemsFr).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </GsdReadableBlock>
      <GsdReadableBlock testId={`${testId}-not-done`}>
        <h3>{locale === "en" ? "Not implemented in Phase 1" : "Non implémenté en Phase 1"}</h3>
        <ul data-testid={`${testId}-not-done-list`}>
          {(locale === "en" ? notDoneEn : notDoneFr).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </GsdReadableBlock>
      <p className="muted" data-testid="security-future-message">
        {locale === "en"
          ? "Local demo only — official traceability and secure storage will require a backend."
          : "Démo locale seulement — la traçabilité officielle et le stockage sécurisé nécessiteront un backend."}
      </p>
    </div>
  );
}

export function StudentProofsExplanation({ testId = "student-proofs-explanation" }) {
  const locale = useLocale();
  return (
    <GsdReadableBlock testId={testId}>
      <h3>{locale === "en" ? "Why documents matter" : "Pourquoi les documents comptent"}</h3>
      <p>
        {locale === "en"
          ? "A title or recognition cannot be complete without an evidence dossier: grade proof, teaching history, recommendations, contribution and authority validation."
          : "Un titre ou une reconnaissance ne peut pas être complet sans dossier de preuves : grade, historique d'enseignement, recommandations, contribution et validation par l'autorité."}
      </p>
      <p className="muted">
        {locale === "en"
          ? "Difference between application and grant — talk to your Sensei. No real upload for students in Phase 1."
          : "Différence entre demande et attribution — parlez à votre Sensei. Aucun téléversement réel pour les étudiants en Phase 1."}
      </p>
    </GsdReadableBlock>
  );
}
